
import React, { useEffect, useRef } from "react";
import "swiper/css/bundle";

export default function AboutTimeline({id}) {
  const swiperContainerRef = useRef(null);
  const swiperInstanceRef = useRef(null);
  const imagesRef = useRef([]);

 useEffect(() => {
  let swiper = null;
  let resizeTimer = null;
  let onResize = null;
  let onWheel = null;
  let onSlideChange = null;
  let isMounted = true;
  const containerEl = swiperContainerRef.current;

  if (!containerEl) return undefined;

  const initSwiper = async () => {
  const swiperModule = await import("swiper/bundle");
  if (!isMounted || !swiperContainerRef.current) return;
  const Swiper = swiperModule.default || swiperModule;

  const MOBILE_THRESHOLD = 767;
  const isMobile = () => window.innerWidth <= MOBILE_THRESHOLD;

  swiper = new Swiper(swiperContainerRef.current, {
    grabCursor: true,
    centeredSlides: false,
    loop: true,
    slidesPerView: isMobile() ? 1 : 3,
    direction: isMobile() ? "horizontal" : "vertical",
    keyboard: { enabled: false },
    speed: 700,
    autoplay: false,
    navigation: false,
    pagination: false,
    // initially enable mousewheel only if wider than threshold and device not touch
    mousewheel:
      !("ontouchstart" in window) && !isMobile()
        ? {
            invert: false,
            sensitivity: 1,
            thresholdDelta: 1,
            releaseOnEdges: true,
            forceToAxis: true,
          }
        : false,
    breakpoints: {
      768: {
        slidesPerView: 3,
      },
    },
  });

  swiperInstanceRef.current = swiper;

  const markActiveImage = (realIndex) => {
    imagesRef.current.forEach((div, i) => {
      if (div) div.classList.toggle("active", i === realIndex);
    });
  };

  markActiveImage(0);

  onSlideChange = () => {
    markActiveImage(swiper.realIndex);
  };
  swiper.on("slideChange", onSlideChange);

  // wheel handling only for screens > MOBILE_THRESHOLD
  let wheelDebounce = 0;
  onWheel = (e) => {
    // only run when viewport is wide enough
    if (isMobile()) return;
    const now = Date.now();
    if (now - wheelDebounce < 50) return;
    wheelDebounce = now;
    const delta = e.deltaY || e.wheelDelta;
    if (delta > 0) swiper.slideNext();
    else if (delta < 0) swiper.slidePrev();
    e.preventDefault();
  };

  // add listener only if desktop initially
  if (!isMobile() && !("ontouchstart" in window)) {
    containerEl.addEventListener("wheel", onWheel, { passive: false });
    // ensure swiper mousewheel module is enabled
    try {
      if (swiper.mousewheel && swiper.params.mousewheel) swiper.mousewheel.enable();
    } catch (e) {}
  }

  // handle resize: toggle mousewheel and the container listener
  onResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const currentlyMobile = isMobile();

      // change direction / slidesPerView if needed
      const newDir = currentlyMobile ? "horizontal" : "vertical";
      const newSlidesPerView = currentlyMobile ? 1 : 3;
      if (swiper.params.direction !== newDir) {
        swiper.changeDirection(newDir, false);
      }
      if (swiper.params.slidesPerView !== newSlidesPerView) {
        swiper.params.slidesPerView = newSlidesPerView;
        swiper.update();
      }

      // toggle container wheel listener and swiper.mousewheel based on width & touch
      if (!currentlyMobile && !("ontouchstart" in window)) {
        // ensure listener exists
        try {
          containerEl.addEventListener("wheel", onWheel, { passive: false });
        } catch (e) {}
        // enable swiper mousewheel if available
        try {
          if (swiper.mousewheel) {
            swiper.params.mousewheel = {
              invert: false,
              sensitivity: 1,
              thresholdDelta: 1,
              releaseOnEdges: true,
              forceToAxis: true,
            };
            swiper.mousewheel.enable();
          }
        } catch (e) {}
      } else {
        // remove listener and disable swiper mousewheel on mobile or touch devices
        try {
          containerEl.removeEventListener("wheel", onWheel);
        } catch (e) {}
        try {
          if (swiper.mousewheel) {
            swiper.mousewheel.disable();
          }
          swiper.params.mousewheel = false;
        } catch (e) {}
      }
    }, 120);
  };

  window.addEventListener("resize", onResize);
  };

  initSwiper();

  return () => {
    isMounted = false;
    try {
      if (onWheel) containerEl.removeEventListener("wheel", onWheel);
    } catch (e) {}
    if (onResize) window.removeEventListener("resize", onResize);
    if (resizeTimer) clearTimeout(resizeTimer);
    if (swiper && swiper.destroy) {
      if (onSlideChange) swiper.off("slideChange", onSlideChange);
      swiper.destroy(true, true);
    }
    swiperInstanceRef.current = null;
  };
}, []);

 
  return (
    <>
    <section className="aboutpage-timeline" id={id}>
        <div className="container">
            <div className="row"> 
                <div className="col-lg-5 col-md-4 order-2 order-md-1 order-lg-1">
                    <div className="timeline-image">
                        <div ref={(el) => (imagesRef.current[0] = el)}>
                            <img src="/image/about-timeline-1.webp" alt="timeline" className="img-responsive"/>
                        </div>

                        <div ref={(el) => (imagesRef.current[1] = el)}>
                            <img src="/image/about-timeline-2.webp" alt="timeline" className="img-responsive"/>
                        </div>

                        <div ref={(el) => (imagesRef.current[2] = el)}>
                            <img src="/image/about-timeline-3.webp" alt="timeline" className="img-responsive"/>
                        </div>

                        <div ref={(el) => (imagesRef.current[3] = el)}>
                            <img src="/image/about-timeline-4.webp" alt="timeline" className="img-responsive"/>
                        </div>

                        <div ref={(el) => (imagesRef.current[4] = el)}>
                            <img src="/image/about-timeline-5.webp" alt="timeline" className="img-responsive"/>
                        </div>

                        <div ref={(el) => (imagesRef.current[5] = el)}>
                            <img src="/image/about-timeline-6.webp" alt="timeline" className="img-responsive"/>
                        </div>

                        <div ref={(el) => (imagesRef.current[6] = el)}>
                            <img src="/image/about-timeline-7.webp" alt="timeline" className="img-responsive"/>
                        </div>
                    </div>
                </div>

                <div className="col-lg-7 col-md-8 order-1 order-md-2 order-lg-2">
                    <div className="abouttime-slider">
                        <p className="abouttime-head">The Story of Saysha-Crafting Spaces Since 2013</p>

                        <div className="swiper" ref={swiperContainerRef}>
                            <div className="swiper-wrapper">
                                <div className="swiper-slide">
                                    <div className="abouttime-slidebox">
                                        <span>2013</span>
                                        <div>
                                            <label>The Idea Was Born</label>
                                            <p>Every great brand begins with a dream. In 2013, the idea was conceived a vision to redefine kitchens and wardrobes with customized, elegant, and functional designs into modern homes.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="swiper-slide">
                                    <div className="abouttime-slidebox">
                                        <span>2014</span>
                                        <div>
                                            <label>The First Step Forward</label>
                                            <p>Founded in 2014, Saysha turned vision into reality by launching its first kitchen and wardrobe display unit in Dwarka, New Delhi marking the start of a brand defined by trust, craftsmanship, and design innovation.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="swiper-slide">
                                    <div className="abouttime-slidebox">
                                        <span>2019</span>
                                        <div>
                                            <label>A Brand Takes Shape</label>
                                            <p>After years of success in retail, Saysha officially launched its own brand identity and established an in-house production unit of 10,000 sq. ft. This milestone allowed complete control over quality, design, and delivery.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="swiper-slide">
                                    <div className="abouttime-slidebox">
                                        <span>2020</span>
                                        <div>
                                            <label>Expanding Reach</label>
                                            <p>With growing demand and customer trust, Saysha opened a new showroom in Bahadurgarh, Haryana, showcasing a wider range of modular furniture solutions.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="swiper-slide">
                                    <div className="abouttime-slidebox">
                                        <span>2022</span>
                                        <div>
                                            <label>Scaling Up Production</label>
                                            <p>To meet increasing demand and introduce advanced manufacturing capabilities, Saysha expanded its production facility to 25,000 sq. ft. equipped with state-of-the-art machinery for mass production of doors, paneling, and modular components.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="swiper-slide">
                                    <div className="abouttime-slidebox">
                                        <span>2024</span>
                                        <div>
                                            <label>The Complete Wood Solution</label>
                                            <p>The brand reached a new milestone with the launch of its flagship showroom in Gurugram, offering complete wood interior solutions - from kitchens and wardrobes to doors, paneling, and millwork all under one roof.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="swiper-slide">
                                    <div className="abouttime-slidebox">
                                        <span>2025</span>
                                        <div>
                                            <label>Production Scale Boost</label>
                                            <p>Scaled up production further to 35000 sq. ft. with involement of new advance machineries and europeans set up standards.</p>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </section>
    </>
  );
}
